import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Truck, ClipboardCheck, AlertTriangle, DollarSign, Inbox as InboxIcon, TrendingUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const brl = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Dashboard() {
  const { user } = useAuth();
  const [sum, setSum] = useState(null);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: t }] = await Promise.all([
          api.get("/reports/summary"),
          api.get("/tasks"),
        ]);
        setSum(s); setTasks(t.slice(0, 6));
      } catch (e) { console.error(e); }
    })();
  }, []);

  const kpis = sum ? [
    { label: "Veículos", value: sum.total_vehicles, icon: Truck, color: "text-primary" },
    { label: "Pendentes", value: sum.pending, icon: InboxIcon, color: "text-amber-500" },
    { label: "Enviadas", value: sum.submitted, icon: ClipboardCheck, color: "text-blue-500" },
    { label: "Aprovadas", value: sum.approved, icon: TrendingUp, color: "text-emerald-500" },
    { label: "Rejeitadas", value: sum.rejected, icon: AlertTriangle, color: "text-red-500" },
    { label: "Custo Total", value: brl(sum.total_cost), icon: DollarSign, color: "text-primary" },
  ] : [];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="dashboard-page">
      <PageHeader title={`Olá, ${user?.name?.split(" ")[0]}`} subtitle="Visão geral da operação de manutenção da frota" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4 border-border bg-card" data-testid={`kpi-${k.label}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{k.label}</div>
              <k.icon className={`w-4 h-4 ${k.color}`} />
            </div>
            <div className="text-2xl font-display font-extrabold">{k.value}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5 border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight">Tarefas recentes</h2>
          <Link to="/inbox"><Button variant="ghost" size="sm" className="gap-1" data-testid="dashboard-see-all">Ver todas <ArrowRight className="w-4 h-4" /></Button></Link>
        </div>
        <div className="space-y-2">
          {tasks.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Nenhuma tarefa ainda. Simule um gatilho Fleetrun para começar.</div>}
          {tasks.map((t) => (
            <Link key={t.id} to={`/inbox/${t.id}`} className="flex items-center justify-between p-3 rounded-md border border-border hover:bg-secondary transition-colors"
              data-testid={`task-row-${t.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{t.template?.title || "Checklist"}</span>
                  <span className="text-xs text-muted-foreground font-mono">#{t.vehicle?.fleet_number} · {t.vehicle?.plate}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Gatilho: {t.trigger_source} {t.trigger_meta?.alert_type ? `· ${t.trigger_meta.alert_type}` : ""}
                </div>
              </div>
              <StatusBadge status={t.status} />
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
